import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  fetchAnalysisJobStatus,
  isTerminalJobStatus,
} from "../slice/AISlice";

/**
 * Polls `GET /api/analysis-jobs/<jobId>/` every `intervalMs` while the
 * passed-in `job` is still running.
 *
 * Contract
 * --------
 * - Caller passes the current `job` (from `selectActiveJobForDocument`)
 *   and a `documentId`. Hook is a no-op if either is missing.
 * - First poll fires immediately so the UI shows fresh state right
 *   after `triggerAnalysisJob` returns (the optimistic synthetic job
 *   has zero progress; the first real poll fills in `total_conditions`
 *   + `conditions[]` so the user isn't staring at "0/0"). After that,
 *   polls every `intervalMs` (default 5000ms — matches the contract
 *   from the original FE brief).
 * - Stops as soon as the cached job's `status` becomes terminal
 *   (`completed | completed_with_errors | failed | cancelled`).
 *   `queued` (BE PR #1898) is deliberately NOT terminal: polling a
 *   queued job is how the UI flips to running the moment the backend
 *   promotes it when the user's running Brief finishes.
 *   Determination is on the latest *cached* job from the slice, which
 *   the previous fulfilled poll just patched — no race window where
 *   the hook keeps polling a finished job for one extra tick.
 * - Pauses when `document.visibilityState !== "visible"` (user
 *   switched tabs / minimized window). Resumes immediately on the
 *   next `visibilitychange → visible`. Saves API load + sidesteps
 *   browser throttling of background `setInterval` (which can stretch
 *   to once-per-minute and surface as fake "stuck job" UX when the
 *   user comes back). The cached state is still authoritative when
 *   paused — re-render just reflects whatever the last poll wrote.
 * - Cleans up on unmount, or when `jobId`/`documentId`/`intervalMs`
 *   change. No leaked intervals across job switches.
 *
 * Why a hook (not a thunk-driven middleware)
 * ------------------------------------------
 * The visibility pause + per-mount lifecycle is easier to reason about
 * locally than a global polling middleware that has to track which
 * documents have a panel open. AnalysisTab is the only consumer for now
 * — Phase 5 (Inbox) might add a second consumer, in which case this
 * hook composes by being mounted twice with different jobIds.
 */
export default function usePollAnalysisJob({
  documentId,
  job,
  intervalMs = 5000,
} = {}) {
  const dispatch = useDispatch();
  // Track the latest job in a ref so the polling tick reads
  // up-to-date status without restarting the interval (which would
  // miss-fire on every status counter increment).
  const jobRef = useRef(job);
  useEffect(() => {
    jobRef.current = job;
  }, [job]);

  const jobId = job?.id || null;
  const jobStatus = job?.status || null;
  // Capture whether the job is currently terminal at hook-effect-run
  // time. The effect re-runs when `jobStatus` changes (driven by
  // poll-fulfilled patches), so a job that becomes terminal cleanly
  // tears down its interval here.
  const terminal = isTerminalJobStatus(jobStatus);

  useEffect(() => {
    if (!documentId || !jobId || terminal) return undefined;

    let cancelled = false;
    let intervalId = null;

    const poll = () => {
      if (cancelled) return;
      // Re-check terminal state from the ref before issuing a poll;
      // a poll can complete and patch the slice between scheduled
      // ticks, and the ref is the source of truth between effect
      // runs.
      const latest = jobRef.current;
      if (latest?.status && isTerminalJobStatus(latest.status)) {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        return;
      }
      dispatch(fetchAnalysisJobStatus({ documentId, jobId }));
    };

    const start = () => {
      if (intervalId !== null) return;
      // Fire immediately so the user sees progress on first paint.
      poll();
      intervalId = setInterval(poll, intervalMs);
    };

    const stop = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const isVisible = () =>
      typeof document === "undefined" ||
      document.visibilityState !== "hidden";

    const handleVisibilityChange = () => {
      if (cancelled) return;
      const latest = jobRef.current;
      if (latest?.status && isTerminalJobStatus(latest.status)) {
        stop();
        return;
      }
      if (isVisible()) {
        start();
      } else {
        stop();
      }
    };

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    if (isVisible()) {
      start();
    }

    return () => {
      cancelled = true;
      stop();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
    };
  }, [dispatch, documentId, jobId, intervalMs, terminal]);
}
